/**
 * The identity every booth API call sends.
 *
 * The booth is not a browser: its next-auth session cookie can lapse or be
 * blocked as third-party, while the bearer token captured at sign-in stays
 * good. Sending only the cookie is what made `/api/features` hand booths the
 * ANONYMOUS baseline and blank their real flag map (fixed in dreambooth#582 /
 * dreambooth-app#267) — so any endpoint that resolves per-account data must
 * send the token as well.
 *
 * `featureFlagsStore` carries an inline copy of this logic predating this
 * module and can adopt it whenever that file is next touched; it is left alone
 * here deliberately, having just been fixed.
 *
 * The dynamic `authStore` import is not stylistic: `authStore` imports the
 * stores that call this, so a static import would close the cycle at
 * module-eval time.
 */
export async function boothAuthHeaders(): Promise<Record<string, string>> {
  try {
    const { useAuthStore } = await import('@renderer/stores/authStore')
    const api = (window as unknown as { api?: { getAuthToken?: () => Promise<string | null> } })
      .api
    const token =
      useAuthStore.getState().user?.cookie ||
      (typeof api?.getAuthToken === 'function' ? await api.getAuthToken().catch(() => null) : null)
    return token ? { Authorization: `Bearer ${token}` } : {}
  } catch {
    // Web build (no preload) or signed out — the same-origin cookie still flows.
    return {}
  }
}
